import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../redux/store';
import { useCandidates } from '../redux/hooks/useCandidates';
import { useStages } from '../redux/hooks/useStages';
import { jobService } from '../services/api/jobService';
import { DocumentUploader } from '../components/document/DocumentUploader';
import { Stage, Job } from '../types/types';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface CandidateFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    jobId: string;
    stageId: string;
    source: string;
    notes: string;
    documents: File[];
}

const AddCandidatePage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Redux hooks
    const { create: createCandidate, uploadDocument, loading: candidateLoading, error: candidateError } = useCandidates();
    const { activeStagesSorted: stages, loading: stagesLoading, ensureDataLoaded: ensureStagesLoaded } = useStages();

    // Local state
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [jobs, setJobs] = useState<Job[]>([]);

    // Toast notification
    const [toast, setToast] = useState<{ show: boolean; message: string; variant: string }>({
        show: false,
        message: '',
        variant: 'success'
    });

    const [formData, setFormData] = useState<CandidateFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobId: '',
        stageId: '',
        source: '',
        notes: '',
        documents: []
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Ensure stages are loaded
                await ensureStagesLoaded();

                // Fetch jobs
                const jobsData = await jobService.getActiveJobs();
                setJobs(jobsData);

                // If there's an initial stage, select it by default
                if (stages.length > 0) {
                    const initialStage = stages.find(stage => stage.displayOrder === 1) || stages[0];
                    setFormData(prev => ({ ...prev, stageId: initialStage.id }));
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load necessary data. Please try again later.');
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [ensureStagesLoaded, stages.length]);

    // Update default stage when stages are loaded
    useEffect(() => {
        if (stages.length > 0 && !formData.stageId) {
            const initialStage = stages.find(stage => stage.displayOrder === 1) || stages[0];
            setFormData(prev => ({ ...prev, stageId: initialStage.id }));
        }
    }, [stages, formData.stageId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (files: File[]) => {
        setFormData(prev => ({
            ...prev,
            documents: files
        }));
    };

    const showToast = (message: string, variant: string = 'success') => {
        setToast({
            show: true,
            message,
            variant
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // First, create the candidate
            const candidateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                jobId: formData.jobId,
                stageId: formData.stageId,
                source: formData.source,
                notes: formData.notes
            };

            const result = await createCandidate(candidateData).unwrap();

            // Then, upload any documents if present
            if (formData.documents.length > 0) {
                try {
                    await Promise.all(
                        formData.documents.map(file =>
                            uploadDocument(result.id, file)
                        )
                    );
                } catch (docError) {
                    console.error('Error uploading documents:', docError);
                    // Don't fail the whole operation for document upload errors
                    showToast('Candidate created successfully, but some documents failed to upload. You can upload them later.', 'warning');
                }
            }

            if (!formData.documents.length || formData.documents.length === 0) {
                showToast('Candidate added successfully!');
            }

            // Wait a moment to show the success message before redirecting
            setTimeout(() => {
                navigate(`/candidates/${result.id}`);
            }, 1500);
        } catch (err: any) {
            console.error('Error creating candidate:', err);
            setError(err.message || 'Failed to add candidate. Please try again.');
            window.scrollTo(0, 0);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    // Show loading if initial data is still loading
    if (loadingData || stagesLoading) {
        return (
            <Container fluid>
                <Row className="mb-4">
                    <Col>
                        <h1 className="mb-0">Add New Candidate</h1>
                    </Col>
                </Row>
                <LoadingSpinner centered />
            </Container>
        );
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-0">Add New Candidate</h1>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {candidateError && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger">
                            {candidateError}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col lg={8}>
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">
                                    <i className="bi bi-person me-2"></i>
                                    Candidate Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>First Name*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Last Name*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email Address*</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Source</Form.Label>
                                            <Form.Select
                                                name="source"
                                                value={formData.source}
                                                onChange={handleChange}
                                                disabled={loading}
                                            >
                                                <option value="">Select Source</option>
                                                <option value="Referral">Referral</option>
                                                <option value="Job Board">Job Board</option>
                                                <option value="LinkedIn">LinkedIn</option>
                                                <option value="Website">Company Website</option>
                                                <option value="Agency">Recruitment Agency</option>
                                                <option value="Other">Other</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">
                                    <i className="bi bi-briefcase me-2"></i>
                                    Job & Stage
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Job Position*</Form.Label>
                                            <Form.Select
                                                name="jobId"
                                                value={formData.jobId}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            >
                                                <option value="">Select Job</option>
                                                {jobs.map(job => (
                                                    <option key={job.id} value={job.id}>
                                                        {job.title} - {job.department}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Initial Stage*</Form.Label>
                                            <Form.Select
                                                name="stageId"
                                                value={formData.stageId}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            >
                                                <option value="">Select Stage</option>
                                                {stages.map(stage => (
                                                    <option key={stage.id} value={stage.id}>
                                                        {stage.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Additional Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Add any additional notes about the candidate..."
                                        disabled={loading}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">
                                    <i className="bi bi-file-earmark-arrow-up me-2"></i>
                                    Documents (Optional)
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <DocumentUploader
                                    onFileChange={handleFileChange}
                                    maxFiles={10}
                                    disabled={loading}
                                />
                                {formData.documents.length > 0 && (
                                    <div className="mt-3">
                                        <small className="text-muted">
                                            {formData.documents.length} file(s) selected for upload
                                        </small>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <div className="d-grid gap-2 position-sticky" style={{ top: '20px' }}>
                            <Button
                                variant="primary"
                                type="submit"
                                size="lg"
                                disabled={loading || candidateLoading}
                            >
                                {loading || candidateLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {formData.documents.length > 0 ? 'Saving & Uploading...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Save Candidate
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={handleCancel}
                                size="lg"
                                disabled={loading || candidateLoading}
                            >
                                Cancel
                            </Button>
                        </div>

                        {/* Show form summary */}
                        {(formData.firstName || formData.lastName) && (
                            <Card className="mt-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0">Summary</h6>
                                </Card.Header>
                                <Card.Body>
                                    {formData.firstName || formData.lastName ? (
                                        <p className="mb-2">
                                            <strong>Name:</strong> {formData.firstName} {formData.lastName}
                                        </p>
                                    ) : null}

                                    {formData.email && (
                                        <p className="mb-2">
                                            <strong>Email:</strong> {formData.email}
                                        </p>
                                    )}

                                    {formData.jobId && (
                                        <p className="mb-2">
                                            <strong>Job:</strong> {jobs.find(j => j.id === formData.jobId)?.title || 'Selected'}
                                        </p>
                                    )}

                                    {formData.stageId && (
                                        <p className="mb-2">
                                            <strong>Stage:</strong> {stages.find(s => s.id === formData.stageId)?.name || 'Selected'}
                                        </p>
                                    )}

                                    {formData.documents.length > 0 && (
                                        <p className="mb-0">
                                            <strong>Documents:</strong> {formData.documents.length} file(s)
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Form>

            {/* Toast Container - Fixed position at the top right */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1050 }}>
                <Toast
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                    show={toast.show}
                    delay={3000}
                    autohide
                    bg={toast.variant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.variant === 'success' ? 'Success' : 'Notification'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    );
};

export default AddCandidatePage;